import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Send, Check } from "lucide-react";

import PageHero from "@/components/layout/PageHero";
import { VisualSection } from "@/components/layout/visualLayout";
import Reveal from "@/components/common/Reveal";
import FloatingDecor from "@/components/motion/FloatingDecor";
import HoverGlow from "@/components/motion/HoverGlow";
import ASSETS from "@/lib/assets";
import api from "@/lib/api";

const topics = ["general", "startup", "investor", "partner", "press"];

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "general",
    message: "",
  });
  const [state, setState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    const topic = searchParams.get("topic");
    if (topic && topics.includes(topic)) {
      setForm((prev) => ({ ...prev, topic }));
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.post("/public/contact", {
        ...form,
        language: i18n.language?.startsWith("en") ? "en" : "de",
      });
      setState({ status: "success", message: t("contact.success") });
      setForm({ name: "", email: "", topic: "general", message: "" });
    } catch (_) {
      setState({ status: "error", message: t("contact.error") });
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        subtitle={t("contact.lead")}
        image={ASSETS.editorial[3]}
        imageAlt={t("contact.eyebrow")}
        testId="contact-hero"
      />

      <VisualSection testId="contact-form-section" divider>
        <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              {state.status === "success" ? (
                <Reveal>
                  <HoverGlow>
                    <div className="wit-card flex flex-col items-start gap-5" data-testid="contact-success">
                      <div className="w-12 h-12 rounded-full bg-ink text-white flex items-center justify-center">
                        <Check size={20} />
                      </div>
                      <h2 className="wit-heading-section">{t("contact.success")}</h2>
                      <Link to="/" className="wit-btn-secondary mt-2">
                        {t("comingSoon.back")} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </HoverGlow>
                </Reveal>
              ) : (
                <Reveal>
                  <HoverGlow>
                    <form onSubmit={onSubmit} className="wit-card flex flex-col gap-5" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <label className="flex flex-col gap-2">
                          <span className="wit-form-label">{t("contact.name")}</span>
                          <input required type="text" value={form.name} onChange={update("name")} className="wit-input" data-testid="contact-name" />
                        </label>
                        <label className="flex flex-col gap-2">
                          <span className="wit-form-label">{t("contact.email")}</span>
                          <input required type="email" value={form.email} onChange={update("email")} className="wit-input" data-testid="contact-email" />
                        </label>
                      </div>

                      <label className="flex flex-col gap-2">
                        <span className="wit-form-label">{t("contact.topic")}</span>
                        <select value={form.topic} onChange={update("topic")} className="wit-input appearance-none" data-testid="contact-topic">
                          {topics.map((k) => (
                            <option key={k} value={k}>
                              {t(`contact.topic${k.charAt(0).toUpperCase()}${k.slice(1)}`)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="flex flex-col gap-2">
                        <span className="wit-form-label">{t("contact.message")}</span>
                        <textarea
                          required
                          rows={6}
                          value={form.message}
                          onChange={update("message")}
                          placeholder={t("contact.messagePlaceholder")}
                          className="wit-input resize-none"
                          data-testid="contact-message"
                        />
                      </label>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
                        <p className="text-xs text-warmGrey">
                          {t("contact.privacyNote")}{" "}
                          <Link to="/privacy" className="underline hover:text-raspberry">
                            {t("footer.privacy")}
                          </Link>
                        </p>
                        <button
                          type="submit"
                          disabled={state.status === "loading"}
                          className="wit-btn-primary disabled:opacity-60"
                          data-testid="contact-submit"
                        >
                          {state.status === "loading" ? t("contact.submitting") : (
                            <>{t("contact.submit")} <Send size={15} /></>
                          )}
                        </button>
                      </div>

                      {state.status === "error" && (
                        <p className="text-coral text-sm" data-testid="contact-error">{state.message}</p>
                      )}
                    </form>
                  </HoverGlow>
                </Reveal>
              )}
            </div>

            <aside className="lg:col-span-2">
              <Reveal delay={0.1}>
                <FloatingDecor range={5} duration={9}>
                  <HoverGlow>
                    <div className="wit-card sticky top-28 overflow-hidden p-0" data-testid="contact-aside">
                      <img src={ASSETS.portrait} alt="Fatma Acar" className="h-48 w-full object-cover" />
                      <div className="p-8">
                        <p className="wit-eyebrow mb-3">{t("meta.tagline")}</p>
                        <p className="font-serif text-lg text-ink leading-snug">{t("contact.lead")}</p>
                      </div>
                    </div>
                  </HoverGlow>
                </FloatingDecor>
              </Reveal>
            </aside>
          </div>
      </VisualSection>
    </div>
  );
}
